<template>
    <div>
        <!-- Landing: subcards list -->
        <div v-if="!currentSubSection" class="ppv2-mt-4">
            <div class="ppv2-flex ppv2-flex-col ppv2-gap-3">
                <div v-for="card in subcards" :key="card.section" class="subcard-item" @click="currentSubSection = card.section">
                    <div class="ppv2-flex-1">
                        <div style="font-size: 15.5px; font-weight: 600; color: rgb(2, 24, 50)">
                            {{ card.title }}
                        </div>
                        <div style="font-size: 13px; color: rgb(107, 116, 128); margin-top: 3px; line-height: 1.4">
                            {{ card.description }}
                        </div>
                    </div>
                    <span class="ppv2-flex" style="color: rgb(203, 210, 220)">
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.7"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="M9 5l7 7-7 7"></path>
                        </svg>
                    </span>
                </div>
            </div>
        </div>

        <!-- Sub-section view -->
        <div
            v-else
            class="planning-section ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-xl ppv2-border ppv2-border-[#E2E6EC] dark:ppv2-border-opacity-20 ppv2-overflow-hidden"
        >
            <div class="planning-section__header ppv2-bg-white dark:ppv2-bg-dark">
                <button type="button" class="planning-section__back" aria-label="Retour" @click="$emit('back-action')">
                    <ArrowLeft :size="20" :stroke-width="1.8" />
                </button>
                <h2 class="planning-section__title poppinsRegular ppv2-text-[#021832] dark:ppv2-text-white">
                    {{ currentSubCardTitle }}
                </h2>
                <div v-if="currentSubSection === 'regular'" class="switch-view" role="group" aria-label="Affichage du planning">
                    <button
                        type="button"
                        :class="{ 'is-active': viewMode === 'list' }"
                        :aria-pressed="viewMode === 'list' ? 'true' : 'false'"
                        title="Vue liste"
                        @click="viewMode = 'list'"
                    >
                        <List :size="18" :stroke-width="1.5" />
                    </button>
                    <button
                        type="button"
                        :class="{ 'is-active': viewMode === 'calendar' }"
                        :aria-pressed="viewMode === 'calendar' ? 'true' : 'false'"
                        title="Vue calendrier"
                        @click="viewMode = 'calendar'"
                    >
                        <CalendarFold :size="18" :stroke-width="1.5" />
                    </button>
                </div>
            </div>

            <div class="planning-section__content ppv2-bg-black ppv2-bg-opacity-[3%] dark:ppv2-bg-dark">
                <!-- Loading -->
                <div v-if="isLoading" class="ppv2-text-center ppv2-py-8">
                    <div
                        class="ppv2-inline-block ppv2-animate-spin ppv2-rounded-full ppv2-h-8 ppv2-w-8 ppv2-border-b-2 ppv2-border-gray-900"
                    ></div>
                    <p class="ppv2-mt-2">{{ $t('resource_planning.loading') }}</p>
                </div>

                <!-- Error -->
                <div v-else-if="error" class="ppv2-bg-red-50 ppv2-border-l-4 ppv2-border-red-400 ppv2-p-4 ppv2-mb-4">
                    <div class="ppv2-flex">
                        <div class="ppv2-flex-shrink-0">
                            <svg
                                class="ppv2-h-5 ppv2-w-5 ppv2-text-red-400"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <div class="ppv2-ml-3">
                            <p class="ppv2-text-sm ppv2-text-red-700">{{ error }}</p>
                        </div>
                    </div>
                </div>

                <!-- Content: Regular -->
                <div v-else-if="currentSubSection === 'regular' && days && Array.isArray(days)">
                    <!-- List view with 2-column layout (Editor on left, Recap card on right) -->
                    <div v-show="viewMode !== 'calendar'" class="ppv2-flex ppv2-gap-6 ppv2-items-start">
                        <!-- Left column: Schedule Editor -->
                        <div
                            class="planning-section__editor ppv2-flex-1 ppv2-min-w-0 ppv2-bg-white dark:ppv2-bg-dark ppv2-border ppv2-border-[#E2E6EC] dark:ppv2-border-opacity-20 ppv2-rounded-xl ppv2-p-5"
                        >
                            <ScheduleDaysEditor
                                ref="scheduleEditor"
                                :allow-copy-to-days="true"
                                :days="days"
                                @change="onScheduleChange"
                                @save="handleScheduleSave"
                            />

                            <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mt-10">
                                <button
                                    class="ppv2-text-slate-500/50 ppv2-text-sm poppinsMedium hover:ppv2-underline ppv2-px-4 ppv2-py-2 ppv2-rounded-lg hover:ppv2-bg-slate-100 transition"
                                    @click="handleBackToLanding"
                                >
                                    {{ $t('common.cancel') }}
                                </button>
                                <BaseButton
                                    type="button"
                                    variant="primary"
                                    :custom-height="42"
                                    :loading="isSaving"
                                    :disabled="isSaving"
                                    @click="saveFromEditor"
                                >
                                    {{ $t('common.save') }}
                                </BaseButton>
                            </div>
                        </div>
                        <!-- Right column: Recap card -->
                        <div class="planning-section__recap ppv2-w-[296px] ppv2-flex-shrink-0 ppv2-sticky ppv2-top-4">
                            <div
                                class="ppv2-bg-white dark:ppv2-bg-dark ppv2-border ppv2-border-[#E2E6EC] dark:ppv2-border-opacity-20 ppv2-rounded-xl ppv2-p-5"
                            >
                                <h3
                                    class="ppv2-text-[11px] poppinsSemiBold ppv2-text-[#64748B] dark:ppv2-text-gray-300 ppv2-tracking-wider ppv2-uppercase ppv2-mb-4"
                                >
                                    {{ $t('resource_planning.recap') || 'RÉCAPITULATIF' }}
                                </h3>
                                <div class="ppv2-flex ppv2-flex-col ppv2-gap-3.5">
                                    <div
                                        v-for="day in liveDraftDays"
                                        :key="'recap-' + day.name"
                                        class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-text-[13px]"
                                    >
                                        <span
                                            class="poppinsMedium"
                                            :class="
                                                day.on
                                                    ? 'ppv2-text-[#021832] dark:ppv2-text-gray-100'
                                                    : 'ppv2-text-[#94A3B8] dark:ppv2-text-gray-400'
                                            "
                                        >
                                            {{ day.label }}
                                        </span>
                                        <span
                                            class="ppv2-tabular-nums text-right"
                                            :class="
                                                day.on
                                                    ? 'poppinsMedium ppv2-text-[#021832] dark:ppv2-text-gray-100'
                                                    : 'ppv2-text-[#94A3B8] dark:ppv2-text-gray-400'
                                            "
                                        >
                                            {{ recapDaySlots(day) }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Calendar view -->
                    <div v-if="viewMode === 'calendar'">
                        <PlanningCalendar :days="days" @edit-day="openEditDayPanel" />
                        <EditPlanningDay
                            :visible="!!editingDay"
                            :title="$t('configuration.cards.schedule.editDayTitle')"
                            :dayInfo="editingDay"
                            @save="updateDay"
                            @close="closeEditDayPanel"
                        />
                    </div>
                </div>

                <!-- Content: Holiday -->
                <div v-else-if="currentSubSection === 'holiday'">
                    <HolidayScheduling />
                </div>

                <!-- Content: Exceptions -->
                <div v-else-if="currentSubSection === 'exceptions'">
                    <ExceptionScheduling />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ScheduleDaysEditor from '../../V3/Experience/components/ScheduleDaysEditor.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import PlanningCalendar from './PlanningCalendar.vue'
import EditPlanningDay from './EditPlanningDay.vue'
import HolidayScheduling from './HolidayScheduling.vue'
import ExceptionScheduling from './ExceptionScheduling.vue'
import ApiEstablishmentManager from '../../../helpers/ApiEstablishmentManager'
import * as hours from '../../../utils/scheduleHours'
import { ArrowLeft, CalendarFold, List } from 'lucide-vue'

export default {
    name: 'ResourcePlanning',
    components: {
        ScheduleDaysEditor,
        BaseButton,
        PlanningCalendar,
        EditPlanningDay,
        HolidayScheduling,
        ExceptionScheduling,
        ArrowLeft,
        CalendarFold,
        List,
    },
    data() {
        return {
            days: null,
            liveDraftDays: [],
            currentSubSection: null,
            viewMode: 'list',
            editingDay: null,
            isLoading: false,
            isSaving: false,
            error: null,
            establishmentId: 1,
        }
    },
    computed: {
        currentSubCardTitle() {
            if (this.currentSubSection === 'regular') return this.$t('resource_planning.title') || "Horaires d'ouverture"
            if (this.currentSubSection === 'holiday') return this.$t('resource_planning.subcards.holiday.title')
            if (this.currentSubSection === 'exceptions') return this.$t('resource_planning.subcards.exceptions.title')
            return ''
        },
        subcards() {
            return [
                {
                    section: 'regular',
                    title: this.$t('resource_planning.subcards.regular.title'),
                    description: this.$t('resource_planning.subcards.regular.description'),
                },
                {
                    section: 'holiday',
                    title: this.$t('resource_planning.subcards.holiday.title'),
                    description: this.$t('resource_planning.subcards.holiday.description'),
                },
                {
                    section: 'exceptions',
                    title: this.$t('resource_planning.subcards.exceptions.title'),
                    description: this.$t('resource_planning.subcards.exceptions.description'),
                },
            ]
        },
    },
    watch: {
        currentSubSection: {
            immediate: true,
            handler(value) {
                this.$emit('section-change', Boolean(value))
                if (value === 'regular' && !this.days) {
                    this.fetchPlanningData()
                }
            },
        },
    },
    methods: {
        onScheduleChange(updatedDays) {
            this.liveDraftDays = updatedDays
        },
        recapDaySlots(day) {
            const nextDay = this.$t('common.scheduleTime.nextDayShort')
            const night =
                day && day.nightFromPrev !== null && day.nightFromPrev !== undefined
                    ? `${this.$t('experience.step4.multiMode.schedule.nightUntil')} ${hours.minutesToHHMM(day.nightFromPrev)}`
                    : ''
            if (!day || !day.on || !day.slots || day.slots.length === 0) {
                const closed = this.$t('resource_planning.closed') || 'Fermé'
                return night ? `${closed} · ${night}` : closed
            }
            const formatted = day.slots.map((slot) => hours.formatSlotLabel(slot, nextDay, '–')).join(' · ')
            return night ? `${night} · ${formatted}` : formatted
        },
        handleBackToLanding() {
            this.currentSubSection = null
            this.viewMode = 'list'
            this.editingDay = null
        },
        mapApiResponseToDays(apiData) {
            const dayMap = [
                { name: 'monday', label: 'Lundi', day: 1 },
                { name: 'tuesday', label: 'Mardi', day: 2 },
                { name: 'wednesday', label: 'Mercredi', day: 3 },
                { name: 'thursday', label: 'Jeudi', day: 4 },
                { name: 'friday', label: 'Vendredi', day: 5 },
                { name: 'saturday', label: 'Samedi', day: 6 },
                { name: 'sunday', label: 'Dimanche', day: 0 },
            ]

            const daysMap = new Map()
            apiData.forEach((item) => {
                const adjustedDay = item.day === 0 ? 6 : item.day - 1
                const dayInfo = dayMap[adjustedDay]
                if (!dayInfo) return

                const period = {
                    start: item.start,
                    end: item.end,
                }

                const lunch =
                    item.lunch_start && item.lunch_end
                        ? {
                              start: item.lunch_start,
                              end: item.lunch_end,
                          }
                        : null

                daysMap.set(adjustedDay, {
                    name: dayInfo.name,
                    label: dayInfo.label,
                    day: dayInfo.day,
                    enabled: item.is_open === 1,
                    periods: [period],
                    lunches: lunch ? [lunch] : [],
                })
            })

            return dayMap.map((day, index) => {
                return (
                    daysMap.get(index) || {
                        name: day.name,
                        label: day.label,
                        enabled: false,
                        periods: [],
                        lunches: [],
                    }
                )
            })
        },

        async fetchPlanningData() {
            this.isLoading = true
            this.error = null

            try {
                const response = await ApiEstablishmentManager.getDefaultScheduling(this.establishmentId)
                if (response && response.data) {
                    this.days = this.mapApiResponseToDays(response.data)
                } else {
                    this.initializeEmptyDays()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.error = 'Failed to load planning data. Please try again later.'
                this.initializeEmptyDays()
            } finally {
                this.isLoading = false
            }
        },

        initializeEmptyDays() {
            const days = [
                { name: 'monday', day: 1, label: 'Lundi', enabled: false, periods: [], lunches: [] },
                { name: 'tuesday', day: 2, label: 'Mardi', enabled: false, periods: [], lunches: [] },
                { name: 'wednesday', day: 3, label: 'Mercredi', enabled: false, periods: [], lunches: [] },
                { name: 'thursday', day: 4, label: 'Jeudi', enabled: false, periods: [], lunches: [] },
                { name: 'friday', day: 5, label: 'Vendredi', enabled: false, periods: [], lunches: [] },
                { name: 'saturday', day: 6, label: 'Samedi', enabled: false, periods: [], lunches: [] },
                { name: 'sunday', day: 0, label: 'Dimanche', enabled: false, periods: [], lunches: [] },
            ]
            this.days = [...days]
        },

        saveFromEditor() {
            if (this.$refs.scheduleEditor) {
                this.$refs.scheduleEditor.save()
            }
        },

        openEditDayPanel(Data) {
            this.editingDay = Data
        },
        closeEditDayPanel() {
            this.editingDay = null
        },
        prepareSchedulePayload(dayInfo) {
            const firstPeriod = dayInfo.periods?.[0] || {}
            const firstLunch = dayInfo.lunches?.[0] || {}

            return {
                date: dayInfo.isDefault ? null : dayInfo.date,
                day: dayInfo.day,
                is_default: dayInfo.isDefault,
                is_open: dayInfo.isOpen,
                start: firstPeriod.start || null,
                end: firstPeriod.end || null,
                lunch_start: firstLunch.start || null,
                lunch_end: firstLunch.end || null,
            }
        },
        async updateDay(dayInfo) {
            try {
                const schedulePayload = this.prepareSchedulePayload(dayInfo)
                await ApiEstablishmentManager.updateSchedulingDay({
                    establishmentId: this.establishmentId,
                    schedule: schedulePayload,
                })

                this.$toast.success(this.$t('resource_planning.schedule_updated'))

                this.closeEditDayPanel()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Failed to update schedule:', error)
                this.$toast.error(this.$t('resource_planning.error_updating_schedule'))
            }
            this.closeEditDayPanel()
        },

        async handleScheduleSave(days) {
            if (!days || !Array.isArray(days)) {
                this.$toast.error(this.$t('resource_planning.errors.no_data'))
                return
            }

            this.days = days
            this.isSaving = true
            this.error = null

            try {
                const formattedData = ApiEstablishmentManager.formatDaysForApi(this.days)
                await ApiEstablishmentManager.setDefaultScheduling(this.establishmentId, formattedData)

                this.$toast.success(this.$t('resource_planning.save_success'))
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Failed to save planning:', error)
                const errorMessage = error.response?.data?.message || this.$t('resource_planning.errors.save_error')
                this.$toast.error(errorMessage)
            } finally {
                this.isSaving = false
            }
        },
    },
}
</script>
<style scoped>
.planning-section {
    margin-top: 16px;
}

.planning-section__content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 30px;
}

.planning-section__header {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr) auto;
    align-items: center;
    gap: 14px;
    min-height: 88px;
    padding: 16px 28px;
    border-bottom: 1px solid #e2e6ec;
}

.dark .planning-section__header {
    border-bottom-color: rgb(226 230 236 / 20%);
}

.planning-section__back {
    display: inline-flex;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid #e2e6ec;
    border-radius: 12px;
    background: #fff;
    color: #021832;
    cursor: pointer;
}

.dark .planning-section__back {
    border-color: rgb(226 230 236 / 30%);
    background: #060b28;
    color: #fff;
}

.planning-section__title {
    margin: 0;
    font-size: 26px;
    line-height: 1.2;
}

.switch-view {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    border-radius: 10px;
    background: #eef1f5;
}

.switch-view button {
    display: inline-flex;
    width: 40px;
    height: 38px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
}

.switch-view button.is-active {
    background: #fff;
    color: #021832;
    box-shadow: 0 1px 3px rgb(2 24 50 / 12%);
}

.dark .switch-view button.is-active {
    background: #060b28;
    color: #fff;
}

@media (max-width: 640px) {
    .planning-section__header {
        grid-template-columns: 44px minmax(0, 1fr);
        gap: 10px;
        padding: 14px 16px;
    }

    .planning-section__title {
        font-size: 21px;
    }

    .switch-view {
        grid-column: 2;
        justify-self: start;
    }

    .planning-section__content {
        padding: 16px;
    }
}

.whiteText {
    color: white;
}
.dark .whiteText {
    color: black;
}
.subcard-item {
    display: flex;
    align-items: center;
    gap: 16px;
    border: 1px solid #e2e6ec;
    border-radius: 14px;
    padding: 18px 20px;
    cursor: pointer;
    transition:
        border-color 0.15s,
        box-shadow 0.15s;
}

.subcard-item:hover {
    border-color: #cbd2dc;
    box-shadow: rgba(2, 24, 50, 0.06) 0px 8px 22px;
}
</style>
